// NewJournalModal.js - Modal för att skapa ny journal

import React, { useState, useEffect } from 'react';
import { getJournalTemplates, createJournalEntry } from './journalApi';
import { resolveAppointmentRef } from '../Calendar/calendarApi';
import apiClient from '../../apiClient';
import { getNotesStatus } from '../../notesClient';
import { useRecordingSession } from '../../recording/RecordingContext';
import './NewJournalModal.css';

// Lokal tid som "YYYY-MM-DDTHH:mm" (formatet <input type="datetime-local"> vill ha).
// Medvetet INTE toISOString() — den ger UTC och skulle skifta timmen.
const pad2 = (n) => String(n).padStart(2, '0');
const toLocalInputValue = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

// Gör om fältets lokala värde till ISO 8601 MED webbläsarens UTC-offset för
// just det datumet (DST-säkert), t.ex. "2026-09-18T14:30:00+02:00". Backend
// normaliserar till klinikens lokala tid, så tidpunkten blir rätt även om
// klientens klocka står i en annan tidszon än servern.
const toOffsetIso = (localValue) => {
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  return `${localValue}:00${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
};

const NewJournalModal = ({ patientId, patient, appointmentId, onClose, onCreated }) => {
  const rec = useRecordingSession();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Formulärdata
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [journalMode, setJournalMode] = useState('manual'); // 'manual' | 'recording' | 'quick'
  const [notesEnabled, setNotesEnabled] = useState(false);

  // Snabb anteckning (mall-lös): fri rubrik, default Telefonkontakt
  const [quickTitle, setQuickTitle] = useState('Telefonkontakt');

  // Besökets datum/tid (Spår 5, backdatering). Anteckningen dateras när
  // BESÖKET var, inte när den skrivs. Default = nu; skickas bara med om
  // användaren ändrat den, så oförändrat värde ger exakt server-"nu" som förut.
  const [visitDefault] = useState(() => toLocalInputValue(new Date()));
  const [visitDateTime, setVisitDateTime] = useState(visitDefault);
  const visitDateEmpty = !visitDateTime;
  const visitDateChanged = !visitDateEmpty && visitDateTime !== visitDefault;
  // max räknas vid varje render (inte fryst vid mount) så "nu" alltid går att välja
  const visitMax = toLocalInputValue(new Date());

  // Bygger fältet som backend tar emot. Returnerar {} när datumet lämnats
  // orört (server-"nu" som förut) eller inte kan tolkas.
  const visitDatePayload = () => {
    if (!visitDateChanged) return {};
    const iso = toOffsetIso(visitDateTime);
    return iso ? { created_at: iso } : {};
  };

  // Diktering är huvudflödet — förvälj det när kliniken har Notes-integrationen
  useEffect(() => {
    let cancelled = false;
    getNotesStatus()
      .then((status) => {
        if (cancelled) return;
        setNotesEnabled(Boolean(status.enabled));
        if (status.enabled) {
          setJournalMode('recording');
          // Lista mikrofonerna så användaren kan välja redan här
          rec.loadMicrophones();
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Ladda mallar
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        let data = await getJournalTemplates();
        // Kallstart för kombokund: tom katalog kan bero på att kliniklänken
        // till Notes inte etablerats ännu. Gör ett token-utbyte (skapar
        // länken) + tvinga mallsynk, och försök igen.
        if (!data.templates?.length) {
          try {
            await apiClient.post('/api/notes/token');
            await apiClient.post('/api/journal/templates/sync');
            data = await getJournalTemplates();
          } catch (linkErr) {
            // Ingen Notes-integration eller Notes onåbart - fortsätt med tom lista
          }
        }
        setTemplates(data.templates || []);
        // Välj första mallen som default om det finns någon
        if (data.templates?.length > 0) {
          setSelectedTemplateId(data.templates[0].template_ref);
        }
      } catch (err) {
        console.error('Error loading templates:', err);
        setError('Kunde inte ladda journalmallar');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Hämta vald mall
  const selectedTemplate = templates.find(t => t.template_ref === selectedTemplateId);

  // Skapa journal
  const handleCreate = async () => {
    if (!selectedTemplateId) {
      setError('Välj en journalmall');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Skapa content baserat på mallens sektioner
      const content = {
        sections: selectedTemplate?.sections?.map(s => ({
          title: s.section_title,
          content: ''
        })) || []
      };

      const journalData = {
        patient_id: patientId,
        template_ref: selectedTemplateId,
        // Tillfälle i en återkommande serie (Spår R) skapas först nu, när
        // anteckningen faktiskt sparas
        appointment_id: (await resolveAppointmentRef(appointmentId)) || null,
        content,
        ...visitDatePayload()
      };

      const newJournal = await createJournalEntry(journalData);
      onCreated(newJournal);
    } catch (err) {
      console.error('Error creating journal:', err);
      setError(err.response?.data?.error || 'Kunde inte skapa journal');
    } finally {
      setCreating(false);
    }
  };

  // Snabb anteckning: skapa en mall-lös post med en enda fri sektion
  // (backend tillåter template_ref = null). Öppnar editorn direkt så man kan
  // skriva och signera.
  const handleCreateQuick = async () => {
    const title = quickTitle.trim() || 'Telefonkontakt';
    try {
      setCreating(true);
      setError(null);
      const newJournal = await createJournalEntry({
        patient_id: patientId,
        // Tillfälle i en återkommande serie (Spår R) skapas först nu, när
        // anteckningen faktiskt sparas
        appointment_id: (await resolveAppointmentRef(appointmentId)) || null,
        content: { sections: [{ title, content: '' }] },
        ...visitDatePayload()
      });
      onCreated(newJournal);
    } catch (err) {
      console.error('Error creating quick note:', err);
      setError(err.response?.data?.error || 'Kunde inte skapa anteckning');
    } finally {
      setCreating(false);
    }
  };

  // "Starta inspelning": skapa utkastet och öppna editorn med dikteringen
  // auto-startad (RecordingPanel pratar direkt med Aion Notes)
  const handleStartRecording = async () => {
    if (!selectedTemplateId) {
      setError('Välj en journalmall');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      const content = {
        sections: selectedTemplate?.sections?.map(s => ({
          title: s.section_title,
          content: ''
        })) || []
      };
      const newJournal = await createJournalEntry({
        patient_id: patientId,
        template_ref: selectedTemplateId,
        // Tillfälle i en återkommande serie (Spår R) skapas först nu, när
        // anteckningen faktiskt sparas
        appointment_id: (await resolveAppointmentRef(appointmentId)) || null,
        content,
        ...visitDatePayload()
      });
      onCreated(newJournal, { autoRecord: true });
    } catch (err) {
      console.error('Error creating journal:', err);
      setError(err.response?.data?.error || 'Kunde inte skapa journal');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="new-journal-modal-overlay" onClick={onClose}>
      <div className="new-journal-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Ny journal</h3>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* Patientinfo */}
          <div className="patient-info-box">
            <i className="fas fa-user"></i>
            <div>
              <strong>{patient?.firstname} {patient?.lastname}</strong>
              <span>{patient?.personnummer}</span>
            </div>
          </div>

          {/* Besöksdatum — längst upp, gäller alla lägen. Default = nu. */}
          <div className={`form-group visit-date-group ${visitDateChanged ? 'changed' : ''} ${visitDateEmpty ? 'empty' : ''}`}>
            <label htmlFor="visit-datetime">
              <i className="fas fa-calendar-day"></i> Besöksdatum och tid
            </label>
            <input
              id="visit-datetime"
              type="datetime-local"
              value={visitDateTime}
              max={visitMax}
              onChange={(e) => setVisitDateTime(e.target.value)}
              disabled={creating}
            />
            <small className="visit-date-hint">
              {visitDateEmpty
                ? 'Ange besöksdatum och tid för att kunna skapa anteckningen.'
                : visitDateChanged
                  ? 'Anteckningen backdateras till besökets datum. Auditloggen visar ändå när den skrevs.'
                  : 'Anteckningen dateras när besöket var – ändra om du skriver i efterhand.'}
            </small>
          </div>

          {loading ? (
            <div className="loading-templates">
              <i className="fas fa-spinner fa-spin"></i> Laddar mallar...
            </div>
          ) : (
            <>
              {/* Mallval — inte relevant för snabb anteckning (mall-lös) */}
              {journalMode !== 'quick' && (
                <div className="form-group">
                  <label>Journalmall</label>
                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    disabled={creating}
                  >
                    <option value="">Välj mall...</option>
                    {templates.map(template => (
                      <option key={template.template_ref} value={template.template_ref}>
                        {template.template_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Rubrik för snabb anteckning */}
              {journalMode === 'quick' && (
                <div className="form-group">
                  <label>Rubrik</label>
                  <input
                    type="text"
                    value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    placeholder="T.ex. Telefonkontakt"
                    disabled={creating}
                  />
                </div>
              )}

              {/* Läge */}
              <div className="form-group">
                <label>Hur vill du skapa journalen?</label>
                <div className="mode-options">
                  {notesEnabled && (
                  <label className={`mode-option ${journalMode === 'recording' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="journalMode"
                      value="recording"
                      checked={journalMode === 'recording'}
                      onChange={() => setJournalMode('recording')}
                      disabled={creating}
                    />
                    <i className="fas fa-microphone"></i>
                    <div>
                      <strong>Starta inspelning</strong>
                      <span>Transkribera med Aion Notes</span>
                    </div>
                  </label>
                  )}
                  <label className={`mode-option ${journalMode === 'manual' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="journalMode"
                      value="manual"
                      checked={journalMode === 'manual'}
                      onChange={() => setJournalMode('manual')}
                      disabled={creating}
                    />
                    <i className="fas fa-keyboard"></i>
                    <div>
                      <strong>Skriv manuellt</strong>
                      <span>Fyll i journalen för hand</span>
                    </div>
                  </label>
                  <label className={`mode-option ${journalMode === 'quick' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="journalMode"
                      value="quick"
                      checked={journalMode === 'quick'}
                      onChange={() => setJournalMode('quick')}
                      disabled={creating}
                    />
                    <i className="fas fa-phone"></i>
                    <div>
                      <strong>Snabb anteckning</strong>
                      <span>Kort notat utan mall (t.ex. telefonkontakt)</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Mikrofonval — dikteringen startar direkt när journalen skapas */}
              {journalMode === 'recording' && rec.microphones.length > 0 && (
                <div className="form-group">
                  <label>Mikrofon</label>
                  <select
                    value={rec.selectedMicId}
                    onChange={(e) => rec.setSelectedMicId(e.target.value)}
                    disabled={creating}
                  >
                    {rec.microphones.map((m) => (
                      <option key={m.deviceId} value={m.deviceId}>{m.label || 'Mikrofon'}</option>
                    ))}
                  </select>
                </div>
              )}

              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={creating}>
            Avbryt
          </button>
          {journalMode === 'quick' ? (
            <button
              className="btn btn-primary"
              onClick={handleCreateQuick}
              disabled={creating || loading || !quickTitle.trim() || visitDateEmpty}
            >
              {creating ? 'Skapar...' : 'Skapa anteckning'}
            </button>
          ) : journalMode === 'manual' ? (
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={creating || !selectedTemplateId || loading || visitDateEmpty}
            >
              {creating ? 'Skapar...' : 'Skapa journal'}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleStartRecording}
              disabled={creating || !selectedTemplateId || loading || visitDateEmpty}
            >
              <i className="fas fa-microphone"></i> {creating ? 'Skapar...' : 'Starta inspelning'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewJournalModal;
